import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  CONFIRMACOES_TABELA_2, MOTIVOS_TABELA_1, bloqueioMotivo1, faltaParaGravar, fatosDaTabela, resumoFaixas, rotuloDecisaoTabela,
  type ConfirmacaoTabela2, type EscolhaTabela, type ItemFatoTabela, type MotivoTabela1,
} from '@/lib/tabelaRenovacaoConferencia';
import { Wifi, Smartphone, CheckCircle2, XCircle, ShieldCheck, MapPin, Loader2, ImageIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { getConvergenciaPedido, registrarTimelineConvergencia } from '@/lib/convergenciaPedido';
import { ImageLightbox } from '@/components/common/ImageLightbox';
import { AudioPlayer } from '@/components/common/AudioPlayer';
import { parseCnpjPayload } from '@/utils/parseCnpjPayload';

interface Props {
  pedidoId: string;
  cnpj?: string | null;
  cpf?: string | null;
  itens?: Array<{ id: string; quadro_atual?: string }>;
}

/**
 * Validação de CONVERGÊNCIA pelo BKO. Aparece no painel do pedido quando
 * bko_validacao='pendente'. Dois cenários:
 *  - Consultor AUTO-DECLAROU convergência ("já tem fibra/móvel") → o BKO confere.
 *  - Consultor declarou "NÃO tem viabilidade de fibra" no endereço da Receita,
 *    com PRINT anexado, contra o que a Claro informa (cobertura = TEM). O BKO
 *    compara endereço RF × print e decide se pune ou não pune.
 * A conferência altera apenas a convergência; nunca cancela nem devolve o pedido.
 */
export function ConvergenciaValidacaoBko({ pedidoId, cnpj, cpf }: Props) {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const [salvando, setSalvando] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [motivoSupervisor, setMotivoSupervisor] = useState('');
  /* A tabela explicada e o ciente (Douglas 22/10/2026, Maquete 3): escolher a
     tabela, marcar o motivo, escrever a conclusão, dar o ciente e confirmar. */
  const [tabelaEscolhida, setTabelaEscolhida] = useState<'1' | '2' | null>(null);
  const [motivo1, setMotivo1] = useState<MotivoTabela1 | null>(null);
  const [confirmados2, setConfirmados2] = useState<ConfirmacaoTabela2[]>([]);
  const [ciente, setCiente] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const podeValidar = !!profile && ['bko', 'master'].includes((profile.role || '').toLowerCase());

  /* O painel abre o pedido MÃE; a marca e a decisão podem estar só no FILHO (a
     fila de 06/10 marcou só o filho, e o card nunca apareceu). Mãe vazio → lê a
     convergência do filho. */
  const { data: convLido, isLoading } = useQuery({
    queryKey: ['convergencia-bko', pedidoId],
    enabled: !!pedidoId,
    staleTime: 30_000,
    queryFn: async () => {
      const doPedido = await getConvergenciaPedido(pedidoId);
      if (doPedido) return doPedido;
      const { data: filhos } = await supabase.from('pedidos').select('id').eq('pedido_mae_id', pedidoId);
      for (const f of filhos || []) {
        const doFilho = await getConvergenciaPedido(f.id);
        if (doFilho) return doFilho;
      }
      return null;
    },
  });

  /* A TABELA DA RENOVAÇÃO SEM DECISÃO (Douglas 22/10/2026): quem diz se falta é
     a mesma fila da Triagem Pedido Fechado (`v_conferencia_tabela_renovacao`),
     não a marca no pedido. Pedido com convergência automática ou sem convergência
     também precisa da Tabela 1 ou 2 para a renovação pagar. */
  const { data: faltaTabela = false, isLoading: carregandoTabela } = useQuery({
    queryKey: ['conferencia-tabela-renovacao-pedido', pedidoId],
    enabled: !!pedidoId,
    staleTime: 30_000,
    queryFn: async () => {
      const { data } = await supabase
        .from('v_conferencia_tabela_renovacao')
        .select('pedido_id')
        .eq('pedido_raiz_id', pedidoId)
        .limit(1);
      return (data || []).length > 0;
    },
  });

  const { data: fatosItens } = useQuery({
    queryKey: ['fatos-tabela-renovacao', pedidoId],
    enabled: !!pedidoId && faltaTabela,
    staleTime: 30_000,
    queryFn: async () => {
      const { data: familia } = await supabase.from('pedidos').select('id').or(`id.eq.${pedidoId},pedido_mae_id.eq.${pedidoId}`);
      const ids = (familia || []).map((p) => p.id);
      const [{ data: itens }, { data: fibras }] = await Promise.all([
        supabase.from('v_planilha_mae_comissao')
          .select('produto, quadro_atual, data_ativacao, mes_trabalho, linhas_telefone')
          .in('pedido_id', ids),
        supabase.from('produtos').select('nome').eq('qtd_fibra', true),
      ]);
      return {
        itens: (itens || []) as ItemFatoTabela[],
        produtosFibra: new Set((fibras || []).map((f) => String(f.nome))),
      };
    },
  });

  // Endereço oficial (Receita) — pra comparar com o print de "sem cobertura".
  const cnpj14 = (cnpj || '').replace(/\D/g, '');
  const { data: rf } = useQuery({
    queryKey: ['rf-endereco-conv-bko', cnpj14],
    enabled: cnpj14.length === 14,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from('cnpj_cache').select('payload').eq('cnpj', cnpj14).maybeSingle();
      return (data as any)?.payload ? parseCnpjPayload((data as any).payload) : null;
    },
  });
  const enderecoRF = rf
    ? `${rf.endereco}${rf.numero ? ', ' + rf.numero : ''}${rf.bairro ? ' - ' + rf.bairro : ''} - ${rf.cidade}/${rf.uf}`
    : '';

  const fmtCnpj = (c?: string | null) =>
    (c || '').replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') || '';

  // Já decidido → selo discreto.
  if (!isLoading && !carregandoTabela && !faltaTabela && convLido?.bko_validacao === 'validado') {
    const resultado = convLido?.bko_resultado === 'tabela_1'
      ? 'BKO conferiu: cliente convergente · renovação na Tabela 1.'
      : convLido?.bko_resultado === 'tabela_2'
        ? 'BKO conferiu: só móvel · renovação na Tabela 2.'
      : convLido?.bko_resultado === 'pune'
      ? 'BKO confirmou cobertura — pune.'
      : convLido?.bko_resultado === 'nao_pune'
        ? 'BKO confirmou sem viabilidade — não pune.'
        : 'Convergência declarada e validada pelo BKO.';
    return (
      <div className="flex items-center gap-1.5 text-xs text-success">
        <ShieldCheck className="h-4 w-4" /> {resultado}
      </div>
    );
  }
  if (!isLoading && !carregandoTabela && !faltaTabela && convLido?.bko_validacao === 'reprovado') {
    return (
      /* §6 do punch do Painel: era texto solto vermelho corrido, sem container —
         sumia no meio da tela. Ganha faixa com filete vermelho por dentro.
          O TEXTO NÃO SE TOCA: `bko_motivo`foi escrito por quem reprovou.
         Normalizar a caixa ou "corrigir" a digitação apaga prova. Só o
         container muda. */
      <div className="flex items-start gap-2 rounded-[9px] border border-danger/25 bg-danger/5 px-3 py-2.5 shadow-[inset_3px_0_0_hsl(var(--danger))]">
        <XCircle className="mt-[1px] h-3.5 w-3.5 flex-shrink-0 text-danger" />
        <p className="text-[11px] leading-[1.5] text-danger">
          Convergência reprovada pelo BKO{convLido.bko_motivo ? ` — ${convLido.bko_motivo}` : ''}.
        </p>
      </div>
    );
  }
  if (isLoading || carregandoTabela) return null;
  if (convLido?.bko_validacao !== 'pendente' && !faltaTabela) return null;
  const conv = convLido ?? ({} as NonNullable<typeof convLido>);

  const provaUrls: string[] = Array.isArray(conv.prova_urls) && conv.prova_urls.length
    ? conv.prova_urls : (conv.prova_url ? [conv.prova_url] : []);
  const temFibra = !!conv.fixa_ja_existente;
  const naoViabilidade = conv.motivo_tipo === 'sem_viabilidade_tecnica'
    || (provaUrls.length > 0 && !temFibra && conv.motivo_tipo !== 'declaracao_cliente');
  const regularizarTabela = (conv as { regularizar_tabela?: boolean }).regularizar_tabela === true
    || (faltaTabela && conv.bko_validacao !== 'pendente');
  const fatos = fatosDaTabela(conv, fatosItens?.itens ?? [], fatosItens?.produtosFibra ?? new Set());
  const escolha: EscolhaTabela = { tabela: tabelaEscolhida, motivo1, confirmados2, ciente, conclusao: motivoSupervisor };
  const faltaTabelaTexto = faltaParaGravar(escolha, fatos);
  const textoSimNao = (v: boolean | null, sim: string, nao: string) => (v === true ? sim : v === false ? nao : 'a conferir no SOLAR');
  const decidir = async (resultado: 'validar_declaracao' | 'pune' | 'nao_pune' | 'declaracao_falsa' | 'tabela_1' | 'tabela_2') => {
    setSalvando(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const confirmouSemViabilidade = resultado === 'nao_pune';
      const declaracaoFalsa = resultado === 'declaracao_falsa';
      const tabelaDecidida = resultado === 'tabela_1' || resultado === 'tabela_2';
      const rotuloResultado = resultado === 'tabela_1' || resultado === 'tabela_2'
        ? rotuloDecisaoTabela({ tabela: resultado === 'tabela_1' ? '1' : '2', motivo1 })
        : resultado === 'pune'
        ? 'TEM COBERTURA · PUNE'
        : declaracaoFalsa
          ? temFibra ? 'FIXA NÃO LOCALIZADA NO CNPJ · PUNE' : 'MÓVEL NÃO LOCALIZADO NO CNPJ · PUNE'
        : resultado === 'nao_pune'
          ? 'NÃO TEM VIABILIDADE · NÃO PUNE'
          : temFibra ? 'FIXA CONFIRMADA NO CNPJ' : 'MÓVEL CONFIRMADO NO CNPJ';
      const novo = {
        ...conv,
        bko_validacao: 'validado',
        bko_validado_por: user?.id ?? null,
        bko_validado_em: new Date().toISOString(),
        bko_motivo: motivoSupervisor.trim() || null,
        bko_resultado: declaracaoFalsa ? 'pune' : resultado,
        justificativa_supervisor: conv.justificativa_supervisor ?? null,
        status: resultado === 'tabela_1' ? 'aprovado com fibra'
          : resultado === 'tabela_2' || naoViabilidade || declaracaoFalsa ? 'aprovado sem fibra' : conv.status,
        ...(tabelaDecidida ? {
          origem_decisao: 'bko_tabela_renovacao',
          bko_tabela_motivo: resultado === 'tabela_1' ? motivo1 : CONFIRMACOES_TABELA_2.map((c) => c.id),
          bko_ciente_em: new Date().toISOString(),
        } : {}),
        classificacao: declaracaoFalsa ? (temFibra ? 'SO_MOVEL' : 'SO_FIBRA') : conv.classificacao,
        fixa_ja_existente: declaracaoFalsa && temFibra ? null : conv.fixa_ja_existente,
        qtd_linhas_movel: declaracaoFalsa && !temFibra ? null : conv.qtd_linhas_movel,
        divergencia_claro: naoViabilidade ? confirmouSemViabilidade : (conv.divergencia_claro ?? null),
      };
      const { data: pedidoAtual, error: pedidoError } = await supabase
        .from('pedidos')
        .select('id, pedido_mae_id')
        .eq('id', pedidoId)
        .maybeSingle();
      if (pedidoError) throw pedidoError;
      const pedidoRaizId = pedidoAtual?.pedido_mae_id || pedidoId;
      const { data: familia, error: familiaError } = await supabase
        .from('pedidos')
        .select('id')
        .or(`id.eq.${pedidoRaizId},pedido_mae_id.eq.${pedidoRaizId}`);
      if (familiaError) throw familiaError;
      const familiaIds = Array.from(new Set([pedidoId, pedidoRaizId, ...((familia || []).map((p: { id: string }) => p.id))]));
      const { data: atualizados, error } = await (supabase as any)
        .from('pedidos')
        .update({ convergencia: novo })
        .in('id', familiaIds)
        .select('id');
      if (error) throw error;
      if (!atualizados || atualizados.length !== familiaIds.length) {
        throw new Error('A decisão não foi sincronizada em todos os registros do pedido. Atualize e tente novamente.');
      }
      await registrarTimelineConvergencia({
        pedidoId,
        cnpj: (cnpj || '').replace(/\D/g, ''),
        descricao: `Conferência BKO — ${rotuloResultado}: ${motivoSupervisor.trim()}`,
        usuarioId: user?.id ?? null,
        payload: novo,
        destaque: true,
      });
      for (const id of familiaIds) queryClient.invalidateQueries({ queryKey: ['convergencia-bko', id] });
      queryClient.invalidateQueries({ queryKey: ['convergencia-pendentes-gerente'] });
      queryClient.invalidateQueries({ queryKey: ['triagem-pedido-fechado'] });
      queryClient.invalidateQueries({ queryKey: ['conferencia-tabela-renovacao'] });
      queryClient.invalidateQueries({ queryKey: ['conferencia-tabela-renovacao-pedido', pedidoId] });
      queryClient.invalidateQueries({ queryKey: ['tabela-renovacao-pedido', pedidoId] });
      toast.success(`Conferência registrada: ${rotuloResultado}.`);
    } catch (e: any) {
      toast.error(`Erro ao validar: ${e?.message || e}`);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <Card className="rounded-2xl border-0 bg-card shadow-[0_5px_18px_hsl(var(--foreground)/0.04)]">
        <CardContent className="space-y-0 p-5 text-sm sm:p-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 font-medium text-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" /> Conferência BKO
            </div>
            <span className="text-[11px] text-muted-foreground">Aguardando análise</span>
          </div>

          {regularizarTabela ? (
            <div className="mt-2 space-y-3">
              <p className="text-xs text-muted-foreground">
                A tabela decide quanto a renovação paga. Confira o CNPJ antes de escolher. O que o CRM já sabe:
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                {[
                  { rotulo: 'Fixa Claro no CNPJ', valor: textoSimNao(fatos.fixaNoCnpj, 'tem', 'não tem'), fonte: 'convergência do pedido' },
                  { rotulo: 'Viabilidade no endereço da Receita', valor: textoSimNao(fatos.viabilidade, 'tem', 'não tem'), fonte: 'consulta de cobertura' },
                  {
                    rotulo: 'Fibra neste pedido',
                    valor: fatos.fibraNoPedido === 'nao' ? 'não' : fatos.fibraNoPedido === 'ativada_no_mes' ? 'sim · ativada no mês' : fatos.fibraNoPedido === 'nao_ativou' ? 'sim · ainda não ativou' : 'sim · ativou em outro mês',
                    fonte: 'itens do pedido',
                  },
                ].map((f) => (
                  <div key={f.rotulo} className="rounded-lg border border-border bg-background px-3 py-2">
                    <div className="text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">{f.rotulo}</div>
                    <div className="text-[13px] font-bold text-foreground">{f.valor}</div>
                    <div className="text-[10.5px] text-muted-foreground">{f.fonte}</div>
                  </div>
                ))}
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {(['1', '2'] as const).map((t) => {
                  const marcada = tabelaEscolhida === t;
                  return (
                    <div key={t} className={`rounded-lg border bg-background px-3 py-2.5 ${marcada ? 'border-primary shadow-[inset_3px_0_0_hsl(var(--primary))]' : 'border-border'}`}>
                      <button
                        type="button"
                        disabled={!podeValidar || salvando}
                        onClick={() => { setTabelaEscolhida(t); setCiente(false); }}
                        className="flex w-full items-center gap-2 text-left text-[13px] font-bold text-foreground"
                      >
                        <span className={`grid h-4 w-4 place-items-center rounded-full border-[1.5px] ${marcada ? 'border-primary' : 'border-muted-foreground'}`}>
                          {marcada && <span className="h-2 w-2 rounded-full bg-primary" />}
                        </span>
                        {t === '1' ? <><Wifi className="h-4 w-4" /> Convergente · Tabela 1</> : <><Smartphone className="h-4 w-4" /> Só móvel · Tabela 2</>}
                      </button>
                      {t === '1' ? (
                        <div className="mt-1.5 space-y-1 text-xs">
                          <p className="text-[11px] text-muted-foreground">Basta um destes. Marque qual:</p>
                          {MOTIVOS_TABELA_1.map((m) => {
                            const bloqueio = bloqueioMotivo1(m.id, fatos);
                            return (
                              <label key={m.id} className={`flex items-start gap-2 ${bloqueio ? 'text-muted-foreground' : ''}`}>
                                <Checkbox
                                  className="mt-0.5"
                                  checked={tabelaEscolhida === '1' && motivo1 === m.id}
                                  disabled={!podeValidar || salvando || !!bloqueio || tabelaEscolhida !== '1'}
                                  onCheckedChange={(v) => { setMotivo1(v ? m.id : null); setCiente(false); }}
                                />
                                <span>{m.texto}{bloqueio && <span className="block text-[10.5px]">{bloqueio}</span>}</span>
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="mt-1.5 space-y-1 text-xs">
                          <p className="text-[11px] text-muted-foreground">Os três precisam ser verdade. Confirme cada um:</p>
                          {CONFIRMACOES_TABELA_2.map((c) => (
                            <label key={c.id} className="flex items-start gap-2">
                              <Checkbox
                                className="mt-0.5"
                                checked={tabelaEscolhida === '2' && confirmados2.includes(c.id)}
                                disabled={!podeValidar || salvando || tabelaEscolhida !== '2'}
                                onCheckedChange={(v) => {
                                  setConfirmados2((atual) => (v ? [...atual.filter((x) => x !== c.id), c.id] : atual.filter((x) => x !== c.id)));
                                  setCiente(false);
                                }}
                              />
                              <span>Conferi: {c.texto}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : naoViabilidade ? (
            /* ───── Caso: consultor diz que NÃO tem viabilidade de fibra ───── */
            <>
              <p className="mt-2 text-xs text-muted-foreground">
                Há uma divergência: o sistema encontrou cobertura, mas o consultor informou <strong className="font-medium text-foreground">sem viabilidade técnica</strong>.
              </p>

              <div className="mt-4 border-t border-border">
                {/* O que a Claro/sistema diz */}
                <div className="flex items-start gap-3 border-b border-border py-3.5 text-xs">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-success/10 text-success">
                    <Wifi className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-success">Claro: tem cobertura</div>
                    <div className="mt-1 flex items-start gap-1 text-muted-foreground">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span><span className="font-medium text-foreground">{conv.cep || 'CEP não informado'}</span> · {enderecoRF || 'Endereço da Receita não localizado'}</span>
                    </div>
                  </div>
                </div>

                {/* O que o consultor diz */}
                <div className="flex flex-wrap items-start gap-3 border-b border-border py-3.5 text-xs">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-danger/10 text-danger">
                    <XCircle className="h-4 w-4" />
                  </span>
                  <div className="min-w-[180px] flex-1">
                    <div className="font-medium text-danger">Consultor: sem viabilidade</div>
                    {conv.motivo && <p className="mt-1 text-muted-foreground">{conv.motivo}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {provaUrls.map((url, i) => {
                      const isAudio = /\.(mp3|wav|ogg|m4a|aac|opus|webm)(\?|$)/i.test(url);
                      return isAudio ? (
                        <AudioPlayer key={url} src={url} className="max-w-[340px]" />
                      ) : (
                        <Button key={url} type="button" size="sm" variant="outline" className="h-[30px] rounded-lg px-2 text-[11px] font-medium" onClick={() => setLightboxUrl(url)}>
                          <ImageIcon className="mr-1 h-3.5 w-3.5" /> Ver print {provaUrls.filter(u => !/\.(mp3|wav|ogg|m4a|aac|opus|webm)(\?|$)/i.test(u)).length > 1 ? i + 1 : ''}
                        </Button>
                      );
                    })}
                    {conv.prova_confirmou_rf && <span className="whitespace-nowrap text-[10px] text-success">✓ endereço confirmado</span>}
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* ───── Caso: consultor auto-declarou "já tem fibra/móvel" ───── */
            <>
              <p className="mt-2 text-xs text-muted-foreground">
                O consultor declarou convergência. Confira se é verdade antes de prosseguir.
              </p>
              <div className="mt-4 border-y border-border py-3.5 text-xs">
                {temFibra ? (
                  <>
                    <div className="flex items-center gap-1.5 font-semibold text-foreground">
                      <Wifi className="h-3.5 w-3.5 text-brand-mid" /> Cliente já tem FIBRA no endereço
                    </div>
                    <div className="text-muted-foreground">Nº do contrato: <span className="text-foreground font-medium">{conv.fixa_ja_existente?.numeroContrato || '—'}</span></div>
                    {provaUrls.length > 0 && (
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {provaUrls.map((url, i) => (
                          <Button key={url} type="button" size="sm" variant="outline" className="h-[30px] rounded-lg px-2 text-[11px] font-medium" onClick={() => setLightboxUrl(url)}>
                            <ImageIcon className="mr-1 h-3.5 w-3.5" /> Ver print {provaUrls.length > 1 ? i + 1 : ''}
                          </Button>
                        ))}
                      </div>
                    )}
                    <div className="flex items-start gap-1 text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>Endereço (oficial): <span className="text-foreground font-medium">{conv.fixa_ja_existente?.endereco || '—'}</span></span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5 font-semibold text-foreground">
                      <Smartphone className="h-3.5 w-3.5 text-primary" /> Cliente já tem MÓVEL no endereço
                    </div>
                    <div className="text-muted-foreground">Linhas: <span className="text-foreground font-medium">{conv.qtd_linhas_movel ?? '—'}</span> · Parceiro Claro Empresas: <span className="text-foreground font-medium">{conv.territorio_aace || '—'}</span></div>
                  </>
                )}
                <div className="text-[11px] text-muted-foreground pt-0.5">
                  {cnpj ? `CNPJ ${fmtCnpj(cnpj)}` : cpf ? `CPF ${String(cpf).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}` : 'documento —'} · cobertura: {conv.cobertura_fibra === true ? 'tem' : conv.cobertura_fibra === false ? 'não' : 'a verificar'}
                </div>
              </div>
            </>
          )}

          {podeValidar ? (
            <div className="mt-4 space-y-3">
              {/* Motivo OBRIGATÓRIO da conferência — vai pra timeline como "Conferência BKO" */}
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-foreground">Conclusão <span className="text-danger">*</span></label>
                <Textarea
                  value={motivoSupervisor}
                  onChange={(e) => setMotivoSupervisor(e.target.value)}
                  rows={2}
                  placeholder="Descreva brevemente o que foi conferido…"
                  className="min-h-[72px] resize-y rounded-xl text-xs"
                />
              </div>
              {regularizarTabela ? (
                <div className="space-y-2">
                  <label className="flex items-start gap-2.5 rounded-[10px] border border-primary/40 bg-primary/[0.05] px-3 py-2.5 text-xs">
                    <Checkbox className="mt-0.5" checked={ciente} disabled={salvando || !tabelaEscolhida} onCheckedChange={(v) => setCiente(!!v)} />
                    <span>
                      <strong>Estou ciente.</strong> Li a regra das duas tabelas, conferi o CNPJ e sei que a tabela muda a comissão
                      da renovação. A decisão fica no meu nome na linha do tempo do pedido.
                    </span>
                  </label>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {faltaTabelaTexto && <span className="text-[10.5px] text-muted-foreground">{faltaTabelaTexto}</span>}
                    <Button size="sm" className="whitespace-nowrap rounded-[10px]" disabled={salvando || !!faltaTabelaTexto} onClick={() => setConfirmando(true)}>
                      {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Gravar {tabelaEscolhida ? `Tabela ${tabelaEscolhida}` : 'a tabela'}</>}
                    </Button>
                  </div>
                  <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar a tabela</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <div className="space-y-2 text-sm">
                            <p>
                              Você vai gravar <strong>{tabelaEscolhida === '1' ? 'Tabela 1 · convergente' : 'Tabela 2 · só móvel'}</strong> neste pedido.
                            </p>
                            <p>Motivo: {rotuloDecisaoTabela({ tabela: tabelaEscolhida, motivo1 }).split(' · ').slice(2).join(' · ')}.</p>
                            <p>Linhas da renovação que passam a pagar por esta tabela: {resumoFaixas(fatos.linhasPorFaixa)}.</p>
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Voltar e revisar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { setConfirmando(false); decidir(tabelaEscolhida === '1' ? 'tabela_1' : 'tabela_2'); }}>
                          Confirmar e gravar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ) : naoViabilidade ? (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button size="sm" variant="outline" className="whitespace-nowrap rounded-[10px] border-border text-danger hover:bg-danger/10" disabled={salvando || !motivoSupervisor.trim()} onClick={() => decidir('pune')}>
                    {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <><XCircle className="h-4 w-4 mr-1" /> Tem cobertura · pune</>}
                  </Button>
                  <Button size="sm" className="whitespace-nowrap rounded-[10px]" disabled={salvando || !motivoSupervisor.trim()} onClick={() => decidir('nao_pune')}>
                    {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Sem viabilidade · não pune</>}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button size="sm" className="whitespace-nowrap rounded-[10px]" disabled={salvando || !motivoSupervisor.trim()} onClick={() => decidir('validar_declaracao')}>
                    {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> {temFibra ? 'Fixa confirmada no CNPJ' : 'Móvel confirmado no CNPJ'}</>}
                  </Button>
                  <Button size="sm" variant="outline" className="whitespace-nowrap rounded-[10px] border-border text-danger hover:bg-danger/10" disabled={salvando || !motivoSupervisor.trim()}
                          onClick={() => decidir('declaracao_falsa')}>
                    <XCircle className="h-4 w-4 mr-1" /> {temFibra ? 'Fixa não localizada · pune' : 'Móvel não localizado · pune'}
                  </Button>
                </div>
              )}
              {!regularizarTabela && !motivoSupervisor.trim() && <p className="text-right text-[10px] text-muted-foreground">Escreva a conclusão da consulta para liberar a decisão.</p>}
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground italic">Apenas o BKO/gestão pode fazer a conferência.</p>
          )}
        </CardContent>
      </Card>

      <ImageLightbox src={lightboxUrl} alt="Print da consulta de cobertura" onClose={() => setLightboxUrl(null)} />
    </>
  );
}
